/**
 * Route guards (deny-by-default, spec §4/§13).
 *
 * - RequireAuth: no session → /login (return target preserved).
 * - RequireSetupComplete: authenticated but setup incomplete → /setup.
 * - RedirectIfSetupIncomplete: wraps the setup wizard itself; once setup is
 *   complete the wizard is unreachable and users are sent to /login.
 * - RequirePermission: renders children only with the given permission.
 */
import { useEffect, type ReactNode } from "react";
import { Link, Navigate, Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { isLocale, type Permission } from "@offertli/shared";
import { apiGet } from "../lib/api.js";
import { useMe, usePermissions } from "../lib/auth.js";
import { Button } from "./ui/Button.js";
import { ProgressSpinner } from "@primereact/ui/progressspinner";
import { EmptyState } from "./ui/EmptyState.js";

function FullPageLoading({ label }: { label: string }) {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-app-background">
      <ProgressSpinner.Root aria-label={label}>
        <ProgressSpinner.Track />
        <ProgressSpinner.Range />
      </ProgressSpinner.Root>
    </div>
  );
}

export function RequireAuth({ children }: { children?: ReactNode }) {
  const { t } = useTranslation();
  const location = useLocation();
  const { data, isLoading, isError } = useMe();

  if (isLoading) return <FullPageLoading label={t("common.loading")} />;
  if (isError || !data) {
    const from = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?from=${from}`} replace />;
  }
  return children ? <>{children}</> : <Outlet />;
}

export function RequireSetupComplete({ children }: { children?: ReactNode }) {
  const { t } = useTranslation();
  const { data, isLoading } = useMe();

  if (isLoading) return <FullPageLoading label={t("common.loading")} />;
  if (data && !data.setup.completed) return <Navigate to="/setup" replace />;
  return children ? <>{children}</> : <Outlet />;
}

export interface SetupStatus {
  completed: boolean;
  ownerInvitationSent: boolean;
  defaultLocale: string | null;
  postmarkConfigured: boolean;
}

export function useSetupStatus() {
  return useQuery({
    queryKey: ["setup", "status"],
    queryFn: () => apiGet<SetupStatus>("/setup/status"),
    staleTime: 30_000,
    retry: false,
  });
}

/**
 * Wraps /setup: while the status is unknown show a loader; once setup has
 * been completed the wizard must not be reachable again.
 */
export function RedirectIfSetupIncomplete({
  children,
}: {
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const { data, isLoading } = useSetupStatus();

  if (isLoading) return <FullPageLoading label={t("common.loading")} />;
  if (data?.completed) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export function RequirePermission({
  permission,
  children,
}: {
  permission: Permission;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const { has } = usePermissions();

  if (!has(permission)) {
    return (
      <div className="py-12">
        <EmptyState
          icon={<i className="pi pi-lock text-2xl" />}
          title={t("forbidden.title")}
          message={t("forbidden.message")}
          action={
            <Button as={Link} to="/" variant="outlined" severity="secondary">
              {t("forbidden.backHome")}
            </Button>
          }
        />
      </div>
    );
  }
  return <>{children}</>;
}

/**
 * Syncs the UI language with the authenticated user's stored locale.
 * Mounted once inside the shell.
 */
export function LocaleSync() {
  const { data } = useMe();
  const { i18n } = useTranslation();
  const locale = data?.user.locale;

  useEffect(() => {
    if (isLocale(locale) && i18n.language !== locale) {
      void i18n.changeLanguage(locale);
    }
  }, [locale, i18n]);

  return null;
}
